import { useState } from 'react';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import { default as useCommerceBasket } from '~/features/commerce-api/hooks/useBasket';
import { notifyError } from '~/shared/utils/errorBoundary/toast';
import { useBasket } from './useBasket';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { Product, ProductItem } from '~/lib/data-contract/salesforce';
import { useTranslation } from '~/shared/utils/translation';

export const useValidateQuantity = () => {
    const { products } = useProducts();
    const { updateItemInBasket } = useCommerceBasket();
    const basket = useBasket();
    const { translate } = useTranslation();
    const [isValidating, setIsValidating] = useState(false);

    const fetchUpdate = async (item: Product) => {
        return await updateItemInBasket(
            { productId: item.id, quantity: item.quantity },
            item.itemId,
        );
    };

    const updateQuantity = async (item: Product, stockAvailable: number) => {
        return await updateItemInBasket(
            { productId: item.id, quantity: stockAvailable },
            item.itemId,
        );
    };

    const remove = async (item: Product, itemId: string) => {
        return await basket.removeItemFromBasket(
            itemId,
            _transformProductToDataLayer(item),
            item.quantity,
        );
    };

    const validateQuantity = async () => {
        let toastActive = 0;

        if (!products) return;

        setIsValidating(true);

        const stocklevels: boolean[] = [];

        const allPromise = products?.map((item) => fetchUpdate(item));

        try {
            const responses = await Promise.all(allPromise);

            responses.forEach((response, index) => {
                response?.productItems?.forEach((value: ProductItem) => {
                    if (!value?.quantity) return;

                    if (
                        value?.quantity > value?.c_availableStock &&
                        value?.c_availableStock !== 0
                    ) {
                        toastActive++;
                        if (toastActive === 1) {
                            notifyError(
                                new Error(
                                    translate('pdp.productBelowStock', {
                                        product: value?.itemText ? value?.itemText : '',
                                    }),
                                ),
                                5000,
                            );
                        }

                        if (products[index].itemId === value.itemId) {
                            updateQuantity(products[index], value?.c_availableStock);
                        }

                        stocklevels.push(false);
                    } else if (value?.c_availableStock === 0) {
                        toastActive++;
                        if (toastActive === 1) {
                            notifyError(
                                new Error(
                                    translate('pdp.productOutOfStock', {
                                        product: value?.itemText ? value?.itemText : '',
                                    }),
                                ),
                                5000,
                            );
                        }

                        remove(products[index], value?.itemId ? value?.itemId : '');
                        stocklevels.push(false);
                    } else {
                        stocklevels.push(true);
                    }
                });
            });
        } catch (error) {
            console.log(error);
        }

        setIsValidating(false);

        return !stocklevels.includes(false);
    };

    return {
        validateQuantity,
        isValidating,
    };
};
